import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Radar, Eye, BarChart3, Search, Linkedin, Twitter } from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { GlassIcon } from "@/components/glass-icon";
import type { LucideIcon } from "lucide-react";
import { seo } from "@/lib/seo";
import { collectionPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

type Item = {
  slug: string;
  label: string;
  group: string;
  body: string;
  icon: LucideIcon;
  metric: string;
};

const products: Item[] = [
  {
    slug: "market-intelligence",
    label: "Market intelligence",
    group: "Intelligence",
    body: "A living dashboard of what your customers are searching for and engaging with.",
    icon: Radar,
    metric: "10k+ signals/wk",
  },
  {
    slug: "ai-visibility-analytics",
    label: "AI visibility analytics",
    group: "Analytics",
    body: "Track how LLMs perceive you against competitors across ChatGPT, Perplexity, Gemini and Claude.",
    icon: Eye,
    metric: "11× citations",
  },
  {
    slug: "cross-channel-social-analytics",
    label: "Cross-channel social analytics",
    group: "Analytics",
    body: "Understand which narratives, posts, and channels are actually building demand.",
    icon: BarChart3,
    metric: "5× engagement",
  },
  {
    slug: "geo-aeo-seo-autopilot",
    label: "GEO / AEO / SEO autopilot",
    group: "Agentic execution",
    body: "Optimise your brand to be cited in Google AI overviews, Gemini, ChatGPT, Claude and Perplexity.",
    icon: Search,
    metric: "+40% organic",
  },
  {
    slug: "linkedin-autopilot",
    label: "LinkedIn autopilot",
    group: "Agentic execution",
    body: "Trend-informed campaigns that keep founders and brand at the center of the conversation.",
    icon: Linkedin,
    metric: "284k reach/mo",
  },
  {
    slug: "x-autopilot",
    label: "X autopilot",
    group: "Agentic execution",
    body: "Daily presence on X, calibrated to what's actually working in your niche right now.",
    icon: Twitter,
    metric: "6.5× engagement",
  },
];

export const Route = createFileRoute("/products/")({
  head: () =>
    seo({
      title: "AI Marketing Products & Autopilot Tools",
      description:
        "Market intelligence, AI visibility analytics, cross-channel social, GEO/AEO/SEO autopilot, LinkedIn autopilot, and X autopilot — one engine.",
      path: "/products",
      jsonLd: [
        collectionPage({
          name: "Axy.digital Products",
          description: "Six products. One autonomous algorithmic marketing engine.",
          url: `${SITE_URL}/products`,
          items: products.map((p) => ({ name: p.label, url: `/products/${p.slug}` })),
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Products", url: "/products" },
        ]),
      ],
    }),
  component: ProductsIndex,
});

function ProductsIndex() {
  return (
    <section className="relative px-6 py-24 overflow-hidden">
      <AuroraBackground />
      <div className="relative max-w-7xl mx-auto">
        <SectionEyebrow>Products</SectionEyebrow>
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6 text-balance">
          One engine. <span className="text-gradient">Six surfaces.</span>
        </h1>
        <p className="text-lg text-white/70 max-w-2xl">
          Each product shares the same market intelligence layer, so every channel learns from every
          other channel.
        </p>
        <div className="grid md:grid-cols-2 gap-5 mt-12">
          {products.map((p) => (
            <Link
              key={p.slug}
              to="/products/$slug"
              params={{ slug: p.slug }}
              className="group relative glass-card p-7 hover-lift hover:-translate-y-1 hover:border-white/25 overflow-hidden transition-all duration-300"
            >
              <div
                aria-hidden
                className="absolute -top-16 -right-16 size-48 rounded-full blur-3xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"
                style={{
                  background: "radial-gradient(circle, rgba(217,70,239,0.55), transparent 65%)",
                }}
              />
              <div className="relative flex items-start gap-4">
                <GlassIcon icon={p.icon} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-pink-soft mb-1.5">
                    {p.group}
                  </p>
                  <h3 className="text-2xl font-semibold text-white mb-2">{p.label}</h3>
                  <p className="text-white/65 mb-4">{p.body}</p>
                  <div className="flex items-center justify-between">
                    <span className="glass-pill px-2.5 py-1 text-[11px] font-medium text-white/85">
                      {p.metric}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm text-pink-soft group-hover:gap-2 transition-all">
                      Explore <ArrowRight className="size-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
